import { MatterDocsFilters } from '@../../../backend/src/types/types';
import { useQuery } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { FetchCaseDocsRes, FetchCaseDocsCountRes } from '@/api/types/queries';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export const useGetDocs = ({
  matterId,
  subsetId,
  page,
  pageSize,
  filters,
  sortBy,
  sortOrder,
  isSubsetCreator = false,
}: {
  matterId: string;
  subsetId: string;
  page: number;
  pageSize: number;
  filters: MatterDocsFilters;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  isSubsetCreator?: boolean;
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<FetchCaseDocsRes>(
    [
      QueryEntity.SubsetDocs,
      { matterId, subsetId },
      {
        page,
        pageSize,
        sortBy,
        sortOrder,
        filters,
        isSubsetCreator,
      },
    ],
    async () => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          page,
          pageSize,
          sortBy: sortBy || undefined,
          sortOrder: sortOrder || undefined,
          filters: filters || undefined,
          isSubsetCreator: isSubsetCreator || undefined,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/docs/search`, fetchConfig);
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!matterId && !!subsetId },
  );
};

export const useGetDocsCount = ({
  matterId,
  subsetId,
  pageSize,
  filters,
  isSubsetCreator = false,
}: {
  matterId: string;
  subsetId: string;
  pageSize: number;
  filters: MatterDocsFilters;
  isSubsetCreator?: boolean;
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<FetchCaseDocsCountRes>(
    [QueryEntity.SubsetDocsCount, { matterId, subsetId, pageSize, filters, isSubsetCreator }],
    async () => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          pageSize,
          filters,
          isSubsetCreator: isSubsetCreator || undefined,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/docs/count`, fetchConfig);
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!matterId && !!subsetId, staleTime: Infinity },
  );
};

export const useGetDocIds = ({
  matterId,
  subsetId,
  filters,
  isSubsetCreator = false,
  enabled = true,
}: {
  matterId: string;
  subsetId: string;
  filters: MatterDocsFilters;
  isSubsetCreator?: boolean;
  enabled?: boolean;
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<{ docIds: string[] }>(
    [QueryEntity.FilteredDocIds, { subsetId, filters, isSubsetCreator }],
    async () => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: { filters, isSubsetCreator: isSubsetCreator || undefined },
      });
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/docs/filtered-ids`,
        fetchConfig,
      );
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!subsetId && enabled },
  );
};
